/**
 * 形状工作台的数据源 —— 按 **颜色 / 形状 / 大小** 把图元分组的只读派生。
 *
 * 与色板（core/palette）的关系：颜色轴的取色口径完全一致（`readPresentation`，
 * 内联 style 优先于呈现属性）；差别只在这里是「一批元素一个组」，而色板是「一个色值一组」。
 *
 * 两个刻意的口径：
 *
 *  · **只有能换形状的图元进分组**（`isConvertibleTag`）：文本 / 图片 / 组 / use 既不能换形状，
 *    进列表也只是噪声 —— 它们出现在「跳过」提示里，而不是混进组里。
 *
 *  · **`dotted` 范围（默认）只收「点状图元」**：跨度 ≤ 图幅 5%。这一条不是审美，而是必需品：
 *    散点图里**同色的图例符号与置信椭圆会被算成同一组**（实测：plotly 的 PCoA 图里
 *    红组 18 个圆 + 1 个红椭圆，椭圆和点同色），一键换形状会顺手把椭圆轮廓毁掉。
 *    用户可以切到 `all` 自己承担。
 *
 * 判定全部走**解析式**（`localBBox`），所以 jsdom / node 里也能单测，不依赖真实布局。
 */
import { normalizeColor } from '../color';
import { walkEditable } from '../dom';
import { localBBox, isConvertibleTag, type ConvertibleTag } from '../geom/local-bbox';
import { viewBoxRect } from '../geom/viewport';
import type { Rect } from '../geom/matrix';
import { readPresentation } from './props';

export type GroupAxis = 'color' | 'shape' | 'size';
export type GroupScope = 'dotted' | 'all';

/** 「点状图元」判据：跨度 ≤ 图幅 5% */
export const DOTTED_RATIO = 0.05;
/** 大小分桶的相对容差（相邻值差 ≤10% 归同一档） */
export const SIZE_TOLERANCE = 0.1;

export const SHAPE_NAME: Record<string, string> = {
  circle: '圆形',
  ellipse: '椭圆',
  rect: '矩形',
  line: '直线',
  polyline: '折线',
  polygon: '多边形',
  path: '路径',
};

/** 形状轴的固定顺序（与左栏图层分组同一套心智；`other` 兜底） */
const SHAPE_ORDER: readonly string[] = ['circle', 'ellipse', 'rect', 'polygon', 'polyline', 'line', 'path'];

export interface ElementGroup {
  /** 稳定 key（颜色 = 色值；形状 = 标签名；大小 = 桶尺寸） */
  key: string;
  axis: GroupAxis;
  /** 行主文字 */
  label: string;
  count: number;
  elements: Element[];
  /** 行左侧色块（只有颜色轴有） */
  swatch: string | null;
  /** 行左侧图标用的标签名（形状轴/尺寸轴按它画图标；null = 画通用方块） */
  shapeTag: string | null;
  /** 代表尺寸（本地坐标系下的 max(w, h)），排序分档用 */
  span: number;
  /** 代表包围盒（尺寸轴 = 桶均值） */
  box: Rect;
  /** 构成明细（行 title 用），如「圆形 17 · 多边形 1」 */
  parts: string;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

interface Candidate {
  element: Element;
  tag: ConvertibleTag;
  box: Rect;
  span: number;
}

const collect = (root: SVGSVGElement, scope: GroupScope): Candidate[] => {
  const doc = viewBoxRect(root);
  const limit = doc && scope === 'dotted' ? Math.max(doc.w, doc.h) * DOTTED_RATIO : null;

  const out: Candidate[] = [];
  walkEditable(root, (element) => {
    const tag = element.tagName.toLowerCase();
    if (!isConvertibleTag(tag)) return;
    const box = localBBox(element);
    if (!box || box.w <= 0 || box.h <= 0) return; // 算不出 / 退化（水平或垂直直线）
    const span = Math.max(box.w, box.h);
    if (limit !== null && span > limit) return;
    out.push({ element, tag, box, span });
  });
  return out;
};

/** 构成明细：同一个组里混着几种标签时才有意义 */
function partsOf(items: readonly Candidate[]): string {
  const counts = new Map<string, number>();
  for (const item of items) counts.set(item.tag, (counts.get(item.tag) ?? 0) + 1);
  if (counts.size <= 1) return '';
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([tag, n]) => `${SHAPE_NAME[tag] ?? tag} ${n}`)
    .join(' · ');
}

/** 取色：fill 优先，缺失/ none / 渐变 → 退回 stroke；都没有 → null（「无颜色」组） */
function paintOf(el: Element): string | null {
  const pick = (v: string | null): string | null => {
    const hex = normalizeColor(v);
    return hex && hex !== '#00000000' ? hex : null;
  };
  const fill = readPresentation(el, 'fill');
  const usable = (v: string | null): boolean => v !== null && v !== 'none' && v !== 'transparent' && !v.startsWith('url(');
  if (usable(fill)) return pick(fill);
  const stroke = readPresentation(el, 'stroke');
  if (usable(stroke)) return pick(stroke);
  return null;
}

function groupByColor(items: readonly Candidate[]): ElementGroup[] {
  const buckets = new Map<string, Candidate[]>();
  for (const item of items) {
    const hex = paintOf(item.element);
    const key = hex ?? 'none';
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }
  const groups = [...buckets.entries()].map(([key, list]) => ({
    key: `c:${key}`,
    axis: 'color' as const,
    label: key === 'none' ? '无颜色' : key.toUpperCase(),
    count: list.length,
    elements: list.map((i) => i.element),
    swatch: key === 'none' ? null : key,
    shapeTag: list[0]?.tag ?? null,
    span: Math.max(...list.map((i) => i.span)),
    box: list[0]?.box ?? { x: 0, y: 0, w: 0, h: 0 },
    parts: partsOf(list),
  }));
  // 元素多的在前；同数量按色值排，保证顺序可复现
  return groups.sort((a, b) => b.count - a.count || (a.key < b.key ? -1 : 1));
}

function groupByShape(items: readonly Candidate[]): ElementGroup[] {
  const buckets = new Map<string, Candidate[]>();
  for (const item of items) {
    const bucket = buckets.get(item.tag);
    if (bucket) bucket.push(item);
    else buckets.set(item.tag, [item]);
  }
  const groups = [...buckets.entries()].map(([tag, list]) => ({
    key: `s:${tag}`,
    axis: 'shape' as const,
    label: SHAPE_NAME[tag] ?? tag,
    count: list.length,
    elements: list.map((i) => i.element),
    swatch: null,
    shapeTag: tag,
    span: Math.max(...list.map((i) => i.span)),
    box: list[0]?.box ?? { x: 0, y: 0, w: 0, h: 0 },
    parts: '',
  }));
  const rank = (tag: string): number => {
    const i = SHAPE_ORDER.indexOf(tag);
    return i < 0 ? SHAPE_ORDER.length : i;
  };
  return groups.sort((a, b) => rank(a.shapeTag ?? '') - rank(b.shapeTag ?? '') || b.count - a.count);
}

/**
 * 大小分档：按跨度**相对容差链式聚簇**（相邻差 ≤10% 归同档），桶的代表尺寸取均值。
 * 为什么不做「精确值分组」：真实文件里浮点尺寸几乎不会完全相等，精确分组会得到一堆单元素组。
 */
function groupBySize(items: readonly Candidate[]): ElementGroup[] {
  const sorted = [...items].sort((a, b) => a.span - b.span);
  const buckets: Candidate[][] = [];
  let current: Candidate[] = [];
  let anchor = 0;
  for (const item of sorted) {
    if (current.length === 0) {
      current = [item];
      anchor = item.span;
      continue;
    }
    if (item.span <= anchor * (1 + SIZE_TOLERANCE)) {
      current.push(item);
      continue;
    }
    buckets.push(current);
    current = [item];
    anchor = item.span;
  }
  if (current.length > 0) buckets.push(current);

  return buckets.map((list) => {
    const meanW = round1(list.reduce((s, i) => s + i.box.w, 0) / list.length);
    const meanH = round1(list.reduce((s, i) => s + i.box.h, 0) / list.length);
    const span = Math.max(...list.map((i) => i.span));
    return {
      key: `z:${meanW}x${meanH}`,
      axis: 'size' as const,
      label: `${meanW} × ${meanH}`,
      count: list.length,
      elements: list.map((i) => i.element),
      swatch: null,
      shapeTag: list[0]?.tag ?? null,
      span,
      box: { x: list[0]?.box.x ?? 0, y: list[0]?.box.y ?? 0, w: meanW, h: meanH },
      parts: partsOf(list),
    };
  });
}

/** 主入口：按轴分组（只读派生，不做缓存） */
export function buildGroups(root: SVGSVGElement, axis: GroupAxis, scope: GroupScope = 'dotted'): ElementGroup[] {
  const items = collect(root, scope);
  if (items.length === 0) return [];
  if (axis === 'color') return groupByColor(items);
  if (axis === 'shape') return groupByShape(items);
  return groupBySize(items);
}

/** 候选元素总数（面板头部「N 个元素」用） */
export const countCandidates = (root: SVGSVGElement, scope: GroupScope = 'dotted'): number =>
  collect(root, scope).length;
